import React from 'react'
import { createBrowserRouter, Navigate, Outlet } from 'react-router-dom'

import { AppLayout } from '@/app/layout/AppLayout'
import { useAuthStore } from '@/store/auth.store'
import { getCurrentUserRoles, canAccessRoute, hasAnyRole } from '@/core/auth'
import type { UserRole } from '@/core/auth'

// Pages
import { LoginPage } from '@/pages/Login'
import { NotFoundPage } from '@/pages/NotFound'

// Dashboard pages
import { FinanceDashboard } from '@/modules/finance/pages/Dashboard'
import { HRDashboard } from '@/modules/hr/pages/Dashboard'
import { EmployeeDashboard } from '@/modules/employee/pages/Dashboard'

// Finance pages
import { ChartsOfAccountsList } from '@/modules/charts-of-accounts/pages/List'
import { ChartsOfAccountsDetail } from '@/modules/charts-of-accounts/pages/Detail'
import { CustomersList } from '@/modules/customers/pages/List'
import { CustomersEdit } from '@/modules/customers/pages/Edit'
import { QuotationsList } from '@/modules/quotations/pages/List'
import { QuotationsDetail } from '@/modules/quotations/pages/Detail'
import QuotationPrint from '@/modules/quotations/pages/Print'
import { InvoicesList } from '@/modules/invoices/pages/List'
import { InvoicesDetail } from '@/modules/invoices/pages/Detail'
import InvoicePrint from '@/modules/invoices/pages/Print'
import { CreditNotesList } from '@/modules/credit-notes/pages/List'
import { CreditNotesDetail } from '@/modules/credit-notes/pages/Detail'
import CreditNotePrint from '@/modules/credit-notes/pages/Print'
import { CustomerAdvancesList } from '@/modules/customer-advances/pages/List'
import { CustomerAdvancesDetail } from '@/modules/customer-advances/pages/Detail'
import { VendorAdvancesList } from '@/modules/vendor-advances/pages/List'
import { VendorAdvancesDetail } from '@/modules/vendor-advances/pages/Detail'
import { PaymentsList } from '@/modules/payments/pages/List'
import { PaymentsDetail } from '@/modules/payments/pages/Detail'
import { ReceiptsList } from '@/modules/receipts/pages/List'
import { ReceiptsDetail } from '@/modules/receipts/pages/Detail'
import { ReceiptReturnsList } from '@/modules/receipt-returns/pages/List'
import { ReceiptReturnsDetail } from '@/modules/receipt-returns/pages/Detail'
import { GeneralEntriesList } from '@/modules/general-entries/pages/List'
import { GeneralEntriesDetail } from '@/modules/general-entries/pages/Detail'
import { ProjectsList } from '@/modules/projects/pages/List'
import { ProjectsDetail } from '@/modules/projects/pages/Detail'
import { CustomerLedgerReport } from '@/modules/finance/pages/reports/CustomerLedgerReport'
import CustomerLedgerReportPrint from '@/modules/finance/pages/reports/CustomerLedgerReportPrint'
import { AccountLedgerReport } from '@/modules/finance/pages/reports/AccountLedgerReport'
import AccountLedgerReportPrint from '@/modules/finance/pages/reports/AccountLedgerReportPrint'
import { VendorLedgerReport } from '@/modules/finance/pages/reports/VendorLedgerReport'
import VendorLedgerReportPrint from '@/modules/finance/pages/reports/VendorLedgerReportPrint'
import { TrialBalanceReport } from '@/modules/finance/pages/reports/TrialBalanceReport'
import TrialBalanceReportPrint from '@/modules/finance/pages/reports/TrialBalanceReportPrint'
import { AgedReceivablesReport } from '@/modules/finance/pages/reports/AgedReceivablesReport'
import AgedReceivablesReportPrint from '@/modules/finance/pages/reports/AgedReceivablesReportPrint'
import { AgedPayablesReport } from '@/modules/finance/pages/reports/AgedPayablesReport'
import AgedPayablesReportPrint from '@/modules/finance/pages/reports/AgedPayablesReportPrint'
import { VatReturnReport } from '@/modules/finance/pages/reports/VatReturnReport'
import { FixedAssetsList } from '@/modules/fixed-assets/pages/List'
import { FixedAssetsEdit } from '@/modules/fixed-assets/pages/Edit'
import { FixedAssetReport } from '@/modules/fixed-assets/pages/FixedAssetReport'
import { AssetDisposalsList } from '@/modules/asset-disposals/pages/List'
import { AssetDisposalsDetail } from '@/modules/asset-disposals/pages/Detail'
import { VendorsList } from '@/modules/vendors/pages/List'
import { VendorsEdit } from '@/modules/vendors/pages/Edit'
import { BillsList } from '@/modules/purchases/pages/BillsList'
import { BillsDetail } from '@/modules/purchases/pages/BillsDetail'
import { RefundsList } from '@/modules/purchases/pages/RefundsList'
import { RefundsDetail } from '@/modules/purchases/pages/RefundsDetail'
import { PaymentReturnsList } from '@/modules/payment-returns/pages/List'
import { PaymentReturnsDetail } from '@/modules/payment-returns/pages/Detail'

// HR pages
import { EmployeesList } from '@/modules/employees/pages/List'
import { EmployeesEdit } from '@/modules/employees/pages/Edit'
import { EmployeesView } from '@/modules/employees/pages/View'
import { Attendance } from '@/modules/hr/pages/Attendance'
import { AttendanceAddCombined } from '@/modules/hr/pages/AttendanceAddCombined'
import { AttendanceAddManual } from '@/modules/hr/pages/AttendanceAddManual'
import { AwardBonusList } from '@/modules/hr/pages/AwardBonusList'
import { AwardBonusDetail } from '@/modules/hr/pages/AwardBonusDetail'
import { IncentiveList } from '@/modules/hr/pages/IncentiveList'
import { IncentiveDetail } from '@/modules/hr/pages/IncentiveDetail'
import { Payroll } from '@/modules/hr/pages/Payroll'
import { PayrollReport } from '@/modules/hr/pages/PayrollReport'
import { PayrollPaymentList } from '@/modules/hr/pages/PayrollPaymentList'
import { PayrollPaymentDetail } from '@/modules/hr/pages/PayrollPaymentDetail'
import { PayrollPaymentView } from '@/modules/hr/pages/PayrollPaymentView'
import { SelfServices } from '@/modules/hr/pages/SelfServices'
import { ContractsList } from '@/modules/contracts/pages/List'
import { ContractsDetail } from '@/modules/contracts/pages/Detail'
import { BanksList } from '@/modules/banks/pages/List'
import { BanksDetail } from '@/modules/banks/pages/Detail'
import { GOSISetting } from '@/modules/hr/pages/GOSISetting'
import { EmpAddressList } from '@/modules/emp-address/pages/List'
import { EmpAddressEdit } from '@/modules/emp-address/pages/Edit'
import { ShiftList } from '@/modules/hr/pages/ShiftList'
import { ShiftDetail } from '@/modules/hr/pages/ShiftDetail'
import { EmpDocumentsList } from '@/modules/emp-documents/pages/List'
import { EmpDocumentsEdit } from '@/modules/emp-documents/pages/Edit'
import { EmpQualificationList } from '@/modules/emp-qualification/pages/List'
import { EmpQualificationEdit } from '@/modules/emp-qualification/pages/Edit'
import { EmpSkillList } from '@/modules/emp-skill/pages/List'
import { EmpSkillEdit } from '@/modules/emp-skill/pages/Edit'
import { TrainingList } from '@/modules/training/pages/List'
import { TrainingEdit } from '@/modules/training/pages/Edit'
import { VehicleList } from '@/modules/vehicle/pages/List'
import { VehicleEdit } from '@/modules/vehicle/pages/Edit'
import { MedicalList } from '@/modules/medical/pages/List'
import { MedicalEdit } from '@/modules/medical/pages/Edit'
import { MediaLinksList } from '@/modules/media-links/pages/List'
import { MediaLinksEdit } from '@/modules/media-links/pages/Edit'

// Security pages
import { UsersList } from '@/modules/security/pages/UsersList'
import { UserDetail } from '@/modules/security/pages/UserDetail'
import { RolesList } from '@/modules/security/pages/RolesList'
import { RoleDetail } from '@/modules/security/pages/RoleDetail'
// Inventory pages
import { InventoryItemsList } from '@/modules/inventory/items/pages/List'
import { InventoryItemEdit } from '@/modules/inventory/items/pages/Edit'
import { ItemBatchList } from '@/modules/inventory/item-batches/pages/List'
import { ItemBatchEdit } from '@/modules/inventory/item-batches/pages/Edit'
import { InventoryCategoryEdit } from '@/modules/inventory/categories/pages/Edit'
import { InventoryWarehouseList } from '@/modules/inventory/warehouses/pages/List'
import { InventoryWarehouseEdit } from '@/modules/inventory/warehouses/pages/Edit'
import { InventoryAdjustmentList } from '@/modules/inventory/adjustments/pages/List'
import { InventoryAdjustmentEdit } from '@/modules/inventory/adjustments/pages/Edit'
import { InventoryMovementList } from '@/modules/inventory/movements/pages/List'
import { InventoryMovementEdit } from '@/modules/inventory/movements/pages/Edit'
import { UnsellableInventoryList } from '@/modules/inventory/unsellable-movement/pages/List'
import { UnsellableInventoryEdit } from '@/modules/inventory/unsellable-movement/pages/Edit'
import { InventoryDispatchList } from '@/modules/inventory/dispatch/pages/List'
import { InventoryDispatchEdit } from '@/modules/inventory/dispatch/pages/Edit'
import { InventoryReceivedList } from '@/modules/inventory/received/pages/List'
import { InventoryReceivedEdit } from '@/modules/inventory/received/pages/Edit'


import { InventoryPurchaseOrdersList } from '@/modules/inventory/purchase-orders/pages/List'
import { InventoryPurchaseOrderEdit } from '@/modules/inventory/purchase-orders/pages/Edit'
import { InventorySalesOrdersList } from '@/modules/inventory/sales-orders/pages/List'
import { InventorySalesOrderEdit } from '@/modules/inventory/sales-orders/pages/Edit'
import { InventoryDeliveryChallanList } from '@/modules/inventory/delivery-challan/pages/List'
import { InventoryDeliveryChallanEdit } from '@/modules/inventory/delivery-challan/pages/Edit'
import { InventoryQuotationsList } from '@/modules/inventory/quotations/pages/List'
import { InventoryQuotationEdit } from '@/modules/inventory/quotations/pages/Edit'
import { GoodsReceiptNotesList } from '@/modules/inventory/goods-receipt-notes/pages/List'
import { GoodsReceiptNotesEdit } from '@/modules/inventory/goods-receipt-notes/pages/Edit'
import { GoodsReturnNotesList } from '@/modules/inventory/goods-return-notes/pages/List'
import { GoodsReturnNotesEdit } from '@/modules/inventory/goods-return-notes/pages/Edit'
import { CogsReport } from '@/modules/inventory/reports/pages/CogsReport'
import CogsReportPrint from '@/modules/inventory/reports/pages/CogsReportPrint'
import { StockReport } from '@/modules/inventory/reports/pages/StockReport'
import StockReportPrint from '@/modules/inventory/reports/pages/StockReportPrint'
import { SalesReport } from '@/modules/inventory/reports/pages/SalesReport'
import SalesReportPrint from '@/modules/inventory/reports/pages/SalesReportPrint'
import { BonusItemsList } from '@/modules/inventory/bonus-items/pages/List'
import { BonusItemsEdit } from '@/modules/inventory/bonus-items/pages/Edit'
import { InventoryDiscountsList } from '@/modules/inventory/discounts/pages/List'
import { InventoryDiscountsEdit } from '@/modules/inventory/discounts/pages/Edit'
import { Stocks } from '@/modules/inventory/pages/Stocks'
import { Vendors } from '@/modules/inventory/pages/Vendors'
import { PointOfSales } from '@/modules/inventory/pages/PointOfSales'

// Supply Chain pages (API pending - localStorage)
import { PurchaseRequisitionsList } from '@/modules/supply-chain/purchase-requisitions/pages/List'
import { PurchaseRequisitionEdit } from '@/modules/supply-chain/purchase-requisitions/pages/Edit'
import { SupplyChainPurchaseOrdersList } from '@/modules/supply-chain/purchase-orders/pages/List'
import { SupplyChainPurchaseOrderEdit } from '@/modules/supply-chain/purchase-orders/pages/Edit'
import { SupplyChainGoodsReceiptNotesList } from '@/modules/supply-chain/goods-receipt-notes/pages/List'
import { SupplyChainGoodsReceiptNotesEdit } from '@/modules/supply-chain/goods-receipt-notes/pages/Edit'

// Approval / Workflow pages
import { ApprovalTemplates } from '@/modules/approvals/pages/ApprovalTemplates'
import { ApprovalJobs } from '@/modules/approvals/pages/ApprovalJobs'
import { ApprovalMasterDetail } from '@/modules/approvals/pages/ApprovalMasterDetail'
import { ApprovalDetail } from '@/modules/approvals/pages/ApprovalDetail'

// Self Services - Loan Request
import { LoanRequestList } from '@/modules/loan-request/pages/List'
import { LoanRequestDetail } from '@/modules/loan-request/pages/Detail'
// Self Services - Expense Claim
import { ExpenseClaimList } from '@/modules/expense-claim/pages/List'
import { ExpenseClaimDetail } from '@/modules/expense-claim/pages/Detail'
// Self Services - Leave Request
import { LeaveRequestList } from '@/modules/leave-request/pages/List'
import { LeaveRequestDetail } from '@/modules/leave-request/pages/Detail'
// Self Services - Hire Employee
import { HireEmployeeList } from '@/modules/hire-employee/pages/List'
import { HireEmployeeDetail } from '@/modules/hire-employee/pages/Detail'
// Self Services - Item Request
import { ItemRequestList } from '@/modules/item-request/pages/List'
import { ItemRequestDetail } from '@/modules/item-request/pages/Detail'
// Self Services - Over Time Request (localStorage until API exists)
import { OvertimeRequestList } from '@/modules/overtime-request/pages/List'
import { OvertimeRequestDetail } from '@/modules/overtime-request/pages/Detail'
// Settings pages
import { Lookups } from '@/modules/settings/pages/Lookups'
import { Taxes } from '@/modules/settings/pages/Taxes'
import { Banks } from '@/modules/settings/pages/Banks'
import { Accounts } from '@/modules/settings/pages/Accounts'
import { BranchesList } from '@/modules/settings/pages/branches/List'
import { BranchesEdit } from '@/modules/settings/pages/branches/Edit'
import { BranchesView } from '@/modules/settings/pages/branches/View'
import { Notifications } from '@/modules/settings/pages/Notifications'
import { LatePolicy } from '@/modules/settings/pages/LatePolicy'
import { LeavePolicy } from '@/modules/settings/pages/LeavePolicy'
import { CompanyBanksList } from '@/modules/company-banks/pages/List'
import { CompanyBanksDetail } from '@/modules/company-banks/pages/Detail'

/**
 * Protected route wrapper that checks authentication
 */
const RequireAuth: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuthStore()

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  return <>{children}</>
}

/**
 * Role-based access control wrapper
 */
const RequireRoles: React.FC<{
  roles: UserRole[]
  children: React.ReactNode
}> = ({ roles, children }) => {
  const { user } = useAuthStore()

  if (!user) {
    return <Navigate to="/login" replace />
  }

  const userRoles = getCurrentUserRoles()
  const hasAccess = hasAnyRole(userRoles, roles)
  
  if (!hasAccess) {
    return <Navigate to="/finance" replace /> // Redirect to default allowed page
  }

  return <>{children}</>
}

/**
 * Route-based access control wrapper
 */
const RequireRouteAccess: React.FC<{
  route: string
  children: React.ReactNode
}> = ({ route, children }) => {
  const { user } = useAuthStore()

  if (!user) {
    return <Navigate to="/login" replace />
  }

  const userRoles = getCurrentUserRoles()
  const hasAccess = canAccessRoute(userRoles, route)
  
  if (!hasAccess) {
    return <Navigate to="/finance" replace />
  }

  return <>{children}</>
}

/**
 * Layout wrapper for authenticated routes
 */
const ProtectedLayout: React.FC = () => {
  return (
    <RequireAuth>
      <AppLayout>
        <Outlet />
      </AppLayout>
    </RequireAuth>
  )
}

/**
 * Router configuration with nested routes and protection
 */
export const router = createBrowserRouter([
  // Public routes
  {
    path: '/login',
    element: <LoginPage />,
  },

  // Protected routes with layout
  {
    path: '/',
    element: <ProtectedLayout />,
    children: [
      // Redirect root to finance dashboard
      {
        index: true,
        element: <Navigate to="/finance" replace />,
      },

      // Dashboard routes — no RBAC here (any authenticated user after branch select); module menus use permissions in Sidebar etc.
      {
        path: 'finance',
        element: <FinanceDashboard />,
      },
      {
        path: 'hr',
        element: <HRDashboard />,
      },
      {
        path: 'employee-dashboard',
        element: <EmployeeDashboard />,
      },

      // Finance Module Routes
      {
        path: 'charts-of-accounts',
        children: [
          {
            index: true,
            element: (
              <RequireRouteAccess route="/charts-of-accounts">
                <ChartsOfAccountsList />
              </RequireRouteAccess>
            ),
          },
          {
            path: 'new',
            element: (
              <RequireRouteAccess route="/charts-of-accounts">
                <ChartsOfAccountsDetail />
              </RequireRouteAccess>
            ),
          },
          {
            path: ':id',
            element: (
              <RequireRouteAccess route="/charts-of-accounts">
                <ChartsOfAccountsDetail />
              </RequireRouteAccess>
            ),
          },
        ],
      },
      {
        path: 'customers',
        children: [
          {
            index: true,
            element: (
              <RequireRouteAccess route="/customers">
                <CustomersList />
              </RequireRouteAccess>
            ),
          },
          {
            path: 'new',
            element: (
              <RequireRoles roles={['Admin', 'Finance']}>
                <CustomersEdit />
              </RequireRoles>
            ),
          },
          {
            path: ':id/edit',
            element: (
              <RequireRoles roles={['Admin', 'Finance']}>
                <CustomersEdit />
              </RequireRoles>
            ),
          },
        ],
      },
      {
        path: 'quotations',
        children: [
          {
            index: true,
            element: (
              <RequireRouteAccess route="/quotations">
                <QuotationsList />
              </RequireRouteAccess>
            ),
          },
          {
            path: 'new',
            element: (
              <RequireRouteAccess route="/quotations">
                <QuotationsDetail />
              </RequireRouteAccess>
            ),
          },
          {
            path: ':id/edit',
            element: (
              <RequireRouteAccess route="/quotations">
                <QuotationsDetail />
              </RequireRouteAccess>
            ),
          },
          {
            path: ':id/print',
            element: (
              <RequireRouteAccess route="/quotations">
                <QuotationPrint />
              </RequireRouteAccess>
            ),
          },
        ],
      },
      {
        path: 'invoices',
        children: [
          {
            index: true,
            element: (
              <RequireRouteAccess route="/invoices">
                <InvoicesList />
              </RequireRouteAccess>
            ),
          },
          {
            path: 'new',
            element: (
              <RequireRouteAccess route="/invoices">
                <InvoicesDetail />
              </RequireRouteAccess>
            ),
          },
          {
            path: ':id/edit',
            element: (
              <RequireRouteAccess route="/invoices">
                <InvoicesDetail />
              </RequireRouteAccess>
            ),
          },
          {
            path: ':id/print',
            element: (
              <RequireRouteAccess route="/invoices">
                <InvoicePrint />
              </RequireRouteAccess>
            ),
          },
        ],
      },
      {
        path: 'credit-notes',
        children: [
          {
            index: true,
            element: (
              <RequireRouteAccess route="/credit-notes">
                <CreditNotesList />
              </RequireRouteAccess>
            ),
          },
          {
            path: 'new',
            element: (
              <RequireRouteAccess route="/credit-notes">
                <CreditNotesDetail />
              </RequireRouteAccess>
            ),
          },
          {
            path: ':id/edit',
            element: (
              <RequireRouteAccess route="/credit-notes">
                <CreditNotesDetail />
              </RequireRouteAccess>
            ),
          },
          {
            path: ':id/print',
            element: (
              <RequireRouteAccess route="/credit-notes">
                <CreditNotePrint />
              </RequireRouteAccess>
            ),
          },
        ],
      },
      {
        path: 'customer-advances',
        children: [
          {
            index: true,
            element: (
              <RequireRouteAccess route="/customer-advances">
                <CustomerAdvancesList />
              </RequireRouteAccess>
            ),
          },
          {
            path: 'new',
            element: (
              <RequireRouteAccess route="/customer-advances">
                <CustomerAdvancesDetail />
              </RequireRouteAccess>
            ),
          },
          {
            path: ':id/edit',
            element: (
              <RequireRouteAccess route="/customer-advances">
                <CustomerAdvancesDetail />
              </RequireRouteAccess>
            ),
          },
        ],
      },
      {
        path: 'receipts',
        children: [
          {
            index: true,
            element: (
              <RequireRouteAccess route="/receipts">
                <ReceiptsList />
              </RequireRouteAccess>
            ),
          },
          {
            path: 'new',
            element: (
              <RequireRouteAccess route="/receipts">
                <ReceiptsDetail />
              </RequireRouteAccess>
            ),
          },
          {
            path: ':id/edit',
            element: (
              <RequireRouteAccess route="/receipts">
                <ReceiptsDetail />
              </RequireRouteAccess>
            ),
          },
        ],
      },
      {
        path: 'receipt-returns',
        children: [
          {
            index: true,
            element: (
              <RequireRouteAccess route="/receipt-returns">
                <ReceiptReturnsList />
              </RequireRouteAccess>
            ),
          },
          {
            path: 'new',
            element: (
              <RequireRouteAccess route="/receipt-returns">
                <ReceiptReturnsDetail />
              </RequireRouteAccess>
            ),
          },
          {
            path: ':id/edit',
            element: (
              <RequireRouteAccess route="/receipt-returns">
                <ReceiptReturnsDetail />
              </RequireRouteAccess>
            ),
          },
        ],
      },
      {
        path: 'general-entries',
        children: [
          {
            index: true,
            element: (
              <RequireRouteAccess route="/general-entries">
                <GeneralEntriesList />
              </RequireRouteAccess>
            ),
          },
          {
            path: 'new',
            element: (
              <RequireRouteAccess route="/general-entries">
                <GeneralEntriesDetail />
              </RequireRouteAccess>
            ),
          },
          {
            path: ':id/edit',
            element: (
              <RequireRouteAccess route="/general-entries">
                <GeneralEntriesDetail />
              </RequireRouteAccess>
            ),
          },
          {
            path: ':id/view',
            element: (
              <RequireRouteAccess route="/general-entries">
                <GeneralEntriesDetail />
              </RequireRouteAccess>
            ),
          },
        ],
      },
      {
        path: 'projects',
        children: [
          {
            index: true,
            element: (
              <RequireRouteAccess route="/projects">
                <ProjectsList />
              </RequireRouteAccess>
            ),
          },
          {
            path: 'new',
            element: (
              <RequireRouteAccess route="/projects">
                <ProjectsDetail />
              </RequireRouteAccess>
            ),
          },
          {
            path: ':id',
            element: (
              <RequireRouteAccess route="/projects">
                <ProjectsDetail />
              </RequireRouteAccess>
            ),
          },
        ],
      },
      {
        path: 'purchases',
        children: [
          {
            path: 'bills',
            children: [
              {
                index: true,
                element: (
                  <RequireRouteAccess route="/purchases/bills">
                    <BillsList />
                  </RequireRouteAccess>
                ),
              },
              {
                path: 'new',
                element: (
                  <RequireRouteAccess route="/purchases/bills">
                    <BillsDetail />
                  </RequireRouteAccess>
                ),
              },
              {
                path: ':id/edit',
                element: (
                  <RequireRouteAccess route="/purchases/bills">
                    <BillsDetail />
                  </RequireRouteAccess>
                ),
              },
            ],
          },
          {
            path: 'refunds',
            children: [
              {
                index: true,
                element: (
                  <RequireRouteAccess route="/purchases/refunds">
                    <RefundsList />
                  </RequireRouteAccess>
                ),
              },
              {
                path: 'new',
                element: (
                  <RequireRouteAccess route="/purchases/refunds">
                    <RefundsDetail />
                  </RequireRouteAccess>
                ),
              },
              {
                path: ':id/edit',
                element: (
                  <RequireRouteAccess route="/purchases/refunds">
                    <RefundsDetail />
                  </RequireRouteAccess>
                ),
              },
            ],
          },
          {
            path: 'payment-returns',
            children: [
              {
                index: true,
                element: (
                  <RequireRouteAccess route="/purchases/payment-returns">
                    <PaymentReturnsList />
                  </RequireRouteAccess>
                ),
              },
              {
                path: 'new',
                element: (
                  <RequireRouteAccess route="/purchases/payment-returns">
                    <PaymentReturnsDetail />
                  </RequireRouteAccess>
                ),
              },
              {
                path: ':id/edit',
                element: (
                  <RequireRouteAccess route="/purchases/payment-returns">
                    <PaymentReturnsDetail />
                  </RequireRouteAccess>
                ),
              },
            ],
          },
          {
            path: 'vendors',
            children: [
              {
                index: true,
                element: (
                  <RequireRouteAccess route="/purchases/vendors">
                    <VendorsList />
                  </RequireRouteAccess>
                ),
              },
              {
                path: 'new',
                element: (
                  <RequireRouteAccess route="/purchases/vendors">
                    <VendorsEdit />
                  </RequireRouteAccess>
                ),
              },
              {
                path: ':id/edit',
                element: (
                  <RequireRouteAccess route="/purchases/vendors">
                    <VendorsEdit />
                  </RequireRouteAccess>
                ),
              },
            ],
          },
          {
            path: 'vendor-advances',
            children: [
              {
                index: true,
                element: (
                  <RequireRouteAccess route="/purchases/vendor-advances">
                    <VendorAdvancesList />
                  </RequireRouteAccess>
                ),
              },
              {
                path: 'new',
                element: (
                  <RequireRouteAccess route="/purchases/vendor-advances">
                    <VendorAdvancesDetail />
                  </RequireRouteAccess>
                ),
              },
              {
                path: ':id/edit',
                element: (
                  <RequireRouteAccess route="/purchases/vendor-advances">
                    <VendorAdvancesDetail />
                  </RequireRouteAccess>
                ),
              },
            ],
          },
          {
            path: 'payments',
            children: [
              {
                index: true,
                element: (
                  <RequireRouteAccess route="/purchases/payments">
                    <PaymentsList />
                  </RequireRouteAccess>
                ),
              },
              {
                path: 'new',
                element: (
                  <RequireRouteAccess route="/purchases/payments">
                    <PaymentsDetail />
                  </RequireRouteAccess>
                ),
              },
              {
                path: ':id/edit',
                element: (
                  <RequireRouteAccess route="/purchases/payments">
                    <PaymentsDetail />
                  </RequireRouteAccess>
                ),
              },
            ],
          },
        ],
      },
      {
        path: 'reports',
        children: [
          {
            path: 'customer-ledger',
            children: [
              {
                index: true,
                element: (
                  <RequireRouteAccess route="/reports/customer-ledger">
                    <CustomerLedgerReport />
                  </RequireRouteAccess>
                ),
              },
              {
                path: 'print',
                element: <CustomerLedgerReportPrint />,
              },
            ],
          },
          {
            path: 'account-ledger',
            children: [
              {
                index: true,
                element: (
                  <RequireRouteAccess route="/reports/account-ledger">
                    <AccountLedgerReport />
                  </RequireRouteAccess>
                ),
              },
              {
                path: 'print',
                element: <AccountLedgerReportPrint />,
              },
            ],
          },
          {
            path: 'vendor-ledger',
            children: [
              {
                index: true,
                element: (
                  <RequireRouteAccess route="/reports/vendor-ledger">
                    <VendorLedgerReport />
                  </RequireRouteAccess>
                ),
              },
              {
                path: 'print',
                element: <VendorLedgerReportPrint />,
              },
            ],
          },
          {
            path: 'trial-balance',
            children: [
              {
                index: true,
                element: (
                  <RequireRouteAccess route="/reports/trial-balance">
                    <TrialBalanceReport />
                  </RequireRouteAccess>
                ),
              },
              {
                path: 'print',
                element: <TrialBalanceReportPrint />,
              },
            ],
          },
          {
            path: 'aged-receivables',
            children: [
              {
                index: true,
                element: (
                  <RequireRouteAccess route="/reports/aged-receivables">
                    <AgedReceivablesReport />
                  </RequireRouteAccess>
                ),
              },
              {
                path: 'print',
                element: <AgedReceivablesReportPrint />,
              },
            ],
          },
          {
            path: 'aged-payables',
            children: [
              {
                index: true,
                element: (
                  <RequireRouteAccess route="/reports/aged-payables">
                    <AgedPayablesReport />
                  </RequireRouteAccess>
                ),
              },
              {
                path: 'print',
                element: <AgedPayablesReportPrint />,
              },
            ],
          },
          {
            path: 'vat-return',
            element: (
              <RequireRouteAccess route="/reports/vat-return">
                <VatReturnReport />
              </RequireRouteAccess>
            ),
          },
        ],
      },
      {
        path: 'fixed-assets',
        children: [
          {
            index: true,
            element: (
              <RequireRouteAccess route="/fixed-assets">
                <FixedAssetsList />
              </RequireRouteAccess>
            ),
          },
          {
            path: 'new',
            element: (
              <RequireRouteAccess route="/fixed-assets">
                <FixedAssetsEdit />
              </RequireRouteAccess>
            ),
          },
          {
            path: ':id/edit',
            element: (
              <RequireRouteAccess route="/fixed-assets">
                <FixedAssetsEdit />
              </RequireRouteAccess>
            ),
          },
          {
            path: 'report',
            element: (
              <RequireRouteAccess route="/fixed-assets">
                <FixedAssetReport />
              </RequireRouteAccess>
            ),
          },
        ],
      },
      {
        path: 'asset-disposals',
        children: [
          {
            index: true,
            element: (
              <RequireRouteAccess route="/asset-disposals">
                <AssetDisposalsList />
              </RequireRouteAccess>
            ),
          },
          {
            path: 'new',
            element: (
              <RequireRouteAccess route="/asset-disposals">
                <AssetDisposalsDetail />
              </RequireRouteAccess>
            ),
          },
          {
            path: ':id/edit',
            element: (
              <RequireRouteAccess route="/asset-disposals">
                <AssetDisposalsDetail />
              </RequireRouteAccess>
            ),
          },
        ],
      },

      // HR Module Routes
      {
        path: 'employees',
        children: [
          {
            index: true,
            element: (
              <RequireRouteAccess route="/employees">
                <EmployeesList />
              </RequireRouteAccess>
            ),
          },
          {
            path: 'new',
            element: (
              <RequireRouteAccess route="/employees">
                <EmployeesEdit />
              </RequireRouteAccess>
            ),
          },
          {
            path: ':id/view',
            element: (
              <RequireRouteAccess route="/employees">
                <EmployeesView />
              </RequireRouteAccess>
            ),
          },
          {
            path: ':id/edit',
            element: (
              <RequireRouteAccess route="/employees">
                <EmployeesEdit />
              </RequireRouteAccess>
            ),
          },
        ],
      },
      {
        path: 'emp-documents',
        children: [
          {
            index: true,
            element: (
              <RequireRouteAccess route="/emp-documents">
                <EmpDocumentsList />
              </RequireRouteAccess>
            ),
          },
          {
            path: 'new',
            element: (
              <RequireRouteAccess route="/emp-documents">
                <EmpDocumentsEdit />
              </RequireRouteAccess>
            ),
          },
          {
            path: ':id/edit',
            element: (
              <RequireRouteAccess route="/emp-documents">
                <EmpDocumentsEdit />
              </RequireRouteAccess>
            ),
          },
        ],
      },
      {
        path: 'emp-addresses',
        children: [
          {
            index: true,
            element: (
              <RequireRouteAccess route="/emp-addresses">
                <EmpAddressList />
              </RequireRouteAccess>
            ),
          },
          {
            path: 'new',
            element: (
              <RequireRouteAccess route="/emp-addresses">
                <EmpAddressEdit />
              </RequireRouteAccess>
            ),
          },
          {
            path: ':id/edit',
            element: (
              <RequireRouteAccess route="/emp-addresses">
                <EmpAddressEdit />
              </RequireRouteAccess>
            ),
          },
        ],
      },
      {
        path: 'qualifications',
        children: [
          {
            index: true,
            element: (
              <RequireRouteAccess route="/qualifications">
                <EmpQualificationList />
              </RequireRouteAccess>
            ),
          },
          {
            path: 'new',
            element: (
              <RequireRouteAccess route="/qualifications">
                <EmpQualificationEdit />
              </RequireRouteAccess>
            ),
          },
          {
            path: ':id/edit',
            element: (
              <RequireRouteAccess route="/qualifications">
                <EmpQualificationEdit />
              </RequireRouteAccess>
            ),
          },
        ],
      },
      {
        path: 'skills',
        children: [
          {
            index: true,
            element: (
              <RequireRouteAccess route="/skills">
                <EmpSkillList />
              </RequireRouteAccess>
            ),
          },
          {
            path: 'new',
            element: (
              <RequireRouteAccess route="/skills">
                <EmpSkillEdit />
              </RequireRouteAccess>
            ),
          },
          {
            path: ':id/edit',
            element: (
              <RequireRouteAccess route="/skills">
                <EmpSkillEdit />
              </RequireRouteAccess>
            ),
          },
        ],
      },
      {
        path: 'trainings',
        children: [
          {
            index: true,
            element: (
              <RequireRouteAccess route="/trainings">
                <TrainingList />
              </RequireRouteAccess>
            ),
          },
          {
            path: 'new',
            element: (
              <RequireRouteAccess route="/trainings">
                <TrainingEdit />
              </RequireRouteAccess>
            ),
          },
          {
            path: ':id/edit',
            element: (
              <RequireRouteAccess route="/trainings">
                <TrainingEdit />
              </RequireRouteAccess>
            ),
          },
        ],
      },
      {
        path: 'vehicles',
        children: [
          {
            index: true,
            element: (
              <RequireRouteAccess route="/vehicles">
                <VehicleList />
              </RequireRouteAccess>
            ),
          },
          {
            path: 'new',
            element: (
              <RequireRouteAccess route="/vehicles">
                <VehicleEdit />
              </RequireRouteAccess>
            ),
          },
          {
            path: ':id/edit',
            element: (
              <RequireRouteAccess route="/vehicles">
                <VehicleEdit />
              </RequireRouteAccess>
            ),
          },
        ],
      },
      {
        path: 'medicals',
        children: [
          {
            index: true,
            element: (
              <RequireRouteAccess route="/medicals">
                <MedicalList />
              </RequireRouteAccess>
            ),
          },
          {
            path: 'new',
            element: (
              <RequireRouteAccess route="/medicals">
                <MedicalEdit />
              </RequireRouteAccess>
            ),
          },
          {
            path: ':id/edit',
            element: (
              <RequireRouteAccess route="/medicals">
                <MedicalEdit />
              </RequireRouteAccess>
            ),
          },
        ],
      },
      {
        path: 'media-links',
        children: [
          {
            index: true,
            element: (
              <RequireRouteAccess route="/media-links">
                <MediaLinksList />
              </RequireRouteAccess>
            ),
          },
          {
            path: 'new',
            element: (
              <RequireRouteAccess route="/media-links">
                <MediaLinksEdit />
              </RequireRouteAccess>
            ),
          },
          {
            path: ':id/edit',
            element: (
              <RequireRouteAccess route="/media-links">
                <MediaLinksEdit />
              </RequireRouteAccess>
            ),
          },
        ],
      },
      {
        path: 'payroll',
        element: <Outlet />,
        children: [
          {
            index: true,
            element: (
              <RequireRouteAccess route="/payroll">
                <Payroll />
              </RequireRouteAccess>
            ),
          },
          {
            path: 'payroll-payment',
            element: (
              <RequireRouteAccess route="/payroll">
                <Outlet />
              </RequireRouteAccess>
            ),
            children: [
              {
                index: true,
                element: <PayrollPaymentList />,
              },
              {
                path: 'new',
                element: <PayrollPaymentDetail />,
              },
              {
                path: ':id/edit',
                element: <PayrollPaymentDetail />,
              },
              {
                path: ':id/view',
                element: <PayrollPaymentView />,
              },
            ],
          },
          {
            path: 'report',
            element: (
              <RequireRouteAccess route="/payroll/report">
                <PayrollReport />
              </RequireRouteAccess>
            ),
          },
          {
            path: 'attendance',
            element: (
              <RequireRouteAccess route="/payroll">
                <Outlet />
              </RequireRouteAccess>
            ),
            children: [
              {
                index: true,
                element: <Attendance />,
              },
              {
                path: 'combined/new',
                element: <AttendanceAddCombined />,
              },
              {
                path: 'manual/new',
                element: <AttendanceAddManual />,
              },
            ],
          },
          {
            path: 'award-bonus',
            element: (
              <RequireRouteAccess route="/payroll">
                <Outlet />
              </RequireRouteAccess>
            ),
            children: [
              {
                index: true,
                element: <AwardBonusList />,
              },
              {
                path: 'new',
                element: <AwardBonusDetail />,
              },
              {
                path: ':id/edit',
                element: <AwardBonusDetail />,
              },
            ],
          },
          {
            path: 'incentives',
            element: (
              <RequireRouteAccess route="/payroll">
                <Outlet />
              </RequireRouteAccess>
            ),
            children: [
              {
                index: true,
                element: <IncentiveList />,
              },
              {
                path: 'new',
                element: <IncentiveDetail />,
              },
              {
                path: ':id/edit',
                element: <IncentiveDetail />,
              },
            ],
          },
        ],
      },
      {
        path: 'self-services',
        element: (
          <RequireRouteAccess route="/self-services">
            <Outlet />
          </RequireRouteAccess>
        ),
        children: [
          {
            index: true,
            element: <SelfServices />,
          },
          {
            path: 'loan-requests',
            children: [
              {
                index: true,
                element: (
                  <RequireRouteAccess route="/self-services/loan-requests">
                    <LoanRequestList />
                  </RequireRouteAccess>
                ),
              },
              {
                path: 'new',
                element: (
                  <RequireRouteAccess route="/self-services/loan-requests">
                    <LoanRequestDetail />
                  </RequireRouteAccess>
                ),
              },
              {
                path: ':id',
                element: (
                  <RequireRouteAccess route="/self-services/loan-requests">
                    <LoanRequestDetail />
                  </RequireRouteAccess>
                ),
              },
            ],
          },
          {
            path: 'expense-claims',
            children: [
              {
                index: true,
                element: (
                  <RequireRouteAccess route="/self-services/expense-claims">
                    <ExpenseClaimList />
                  </RequireRouteAccess>
                ),
              },
              {
                path: 'new',
                element: (
                  <RequireRouteAccess route="/self-services/expense-claims">
                    <ExpenseClaimDetail />
                  </RequireRouteAccess>
                ),
              },
              {
                path: ':id',
                element: (
                  <RequireRouteAccess route="/self-services/expense-claims">
                    <ExpenseClaimDetail />
                  </RequireRouteAccess>
                ),
              },
            ],
          },
          {
            path: 'leave-requests',
            children: [
              {
                index: true,
                element: (
                  <RequireRouteAccess route="/self-services/leave-requests">
                    <LeaveRequestList />
                  </RequireRouteAccess>
                ),
              },
              {
                path: 'new',
                element: (
                  <RequireRouteAccess route="/self-services/leave-requests">
                    <LeaveRequestDetail />
                  </RequireRouteAccess>
                ),
              },
              {
                path: ':id',
                element: (
                  <RequireRouteAccess route="/self-services/leave-requests">
                    <LeaveRequestDetail />
                  </RequireRouteAccess>
                ),
              },
            ],
          },
          {
            path: 'hire-employee',
            children: [
              {
                index: true,
                element: (
                  <RequireRouteAccess route="/self-services/hire-employee">
                    <HireEmployeeList />
                  </RequireRouteAccess>
                ),
              },
              {
                path: 'new',
                element: (
                  <RequireRouteAccess route="/self-services/hire-employee">
                    <HireEmployeeDetail />
                  </RequireRouteAccess>
                ),
              },
              {
                path: ':id',
                element: (
                  <RequireRouteAccess route="/self-services/hire-employee">
                    <HireEmployeeDetail />
                  </RequireRouteAccess>
                ),
              },
            ],
          },
          {
            path: 'item-requests',
            children: [
              {
                index: true,
                element: (
                  <RequireRouteAccess route="/self-services/item-requests">
                    <ItemRequestList />
                  </RequireRouteAccess>
                ),
              },
              {
                path: 'new',
                element: (
                  <RequireRouteAccess route="/self-services/item-requests">
                    <ItemRequestDetail />
                  </RequireRouteAccess>
                ),
              },
              {
                path: ':id',
                element: (
                  <RequireRouteAccess route="/self-services/item-requests">
                    <ItemRequestDetail />
                  </RequireRouteAccess>
                ),
              },
            ],
          },
          {
            path: 'overtime-requests',
            children: [
              {
                index: true,
                element: (
                  <RequireRouteAccess route="/self-services/overtime-requests">
                    <OvertimeRequestList />
                  </RequireRouteAccess>
                ),
              },
              {
                path: 'new',
                element: (
                  <RequireRouteAccess route="/self-services/overtime-requests">
                    <OvertimeRequestDetail />
                  </RequireRouteAccess>
                ),
              },
              {
                path: ':id',
                element: (
                  <RequireRouteAccess route="/self-services/overtime-requests">
                    <OvertimeRequestDetail />
                  </RequireRouteAccess>
                ),
              },
            ],
          },
        ],
      },
      {
        path: 'gosi-setting',
        element: (
          <RequireRouteAccess route="/gosi-setting">
            <GOSISetting />
          </RequireRouteAccess>
        ),
      },
      {
        path: 'shifts',
        children: [
          {
            index: true,
            element: (
              <RequireRouteAccess route="/shifts">
                <ShiftList />
              </RequireRouteAccess>
            ),
          },
          {
            path: 'new',
            element: (
              <RequireRouteAccess route="/shifts">
                <ShiftDetail />
              </RequireRouteAccess>
            ),
          },
          {
            path: ':id/edit',
            element: (
              <RequireRouteAccess route="/shifts">
                <ShiftDetail />
              </RequireRouteAccess>
            ),
          },
        ],
      },
      {
        path: 'contracts',
        children: [
          {
            index: true,
            element: (
              <RequireRouteAccess route="/contracts">
                <ContractsList />
              </RequireRouteAccess>
            ),
          },
          {
            path: 'new',
            element: (
              <RequireRouteAccess route="/contracts">
                <ContractsDetail />
              </RequireRouteAccess>
            ),
          },
          {
            path: ':id/edit',
            element: (
              <RequireRouteAccess route="/contracts">
                <ContractsDetail />
              </RequireRouteAccess>
            ),
          },
          {
            path: ':id',
            element: (
              <RequireRouteAccess route="/contracts">
                <ContractsDetail />
              </RequireRouteAccess>
            ),
          },
        ],
      },
      {
        path: 'banks',
        children: [
          {
            index: true,
            element: (
              <RequireRouteAccess route="/banks">
                <BanksList />
              </RequireRouteAccess>
            ),
          },
          {
            path: 'new',
            element: (
              <RequireRouteAccess route="/banks">
                <BanksDetail />
              </RequireRouteAccess>
            ),
          },
          {
            path: ':id/edit',
            element: (
              <RequireRouteAccess route="/banks">
                <BanksDetail />
              </RequireRouteAccess>
            ),
          },
        ],
      },
      {
        path: 'banks',
        children: [
          {
            index: true,
            element: (
              <RequireRouteAccess route="/banks">
                <BanksList />
              </RequireRouteAccess>
            ),
          },
          {
            path: 'new',
            element: (
              <RequireRouteAccess route="/banks">
                <BanksDetail />
              </RequireRouteAccess>
            ),
          },
          {
            path: ':id/edit',
            element: (
              <RequireRouteAccess route="/banks">
                <BanksDetail />
              </RequireRouteAccess>
            ),
          },
        ],
      },

      // Inventory — items (list + form; API pending)
      // Flat paths so the layout Outlet always receives a leaf route (nested parent-only routes can 404 in RR6).
      {
        path: 'inventory/items/new',
        element: (
          <RequireRouteAccess route="/inventory/items">
            <InventoryItemEdit />
          </RequireRouteAccess>
        ),
      },
      {
        path: 'inventory/items/:id/edit',
        element: (
          <RequireRouteAccess route="/inventory/items">
            <InventoryItemEdit />
          </RequireRouteAccess>
        ),
      },
      {
        path: 'inventory/items',
        element: (
          <RequireRouteAccess route="/inventory/items">
            <InventoryItemsList />
          </RequireRouteAccess>
        ),
      },

      // Inventory — item batches (list + form; API pending)
      {
        path: 'inventory/item-batches/new',
        element: (
          <RequireRouteAccess route="/inventory/item-batches">
            <ItemBatchEdit />
          </RequireRouteAccess>
        ),
      },
      {
        path: 'inventory/item-batches/:id/edit',
        element: (
          <RequireRouteAccess route="/inventory/item-batches">
            <ItemBatchEdit />
          </RequireRouteAccess>
        ),
      },
      {
        path: 'inventory/item-batches',
        element: (
          <RequireRouteAccess route="/inventory/item-batches">
            <ItemBatchList />
          </RequireRouteAccess>
        ),
      },

      // Inventory — categories (form only at index; API pending)
      {
        path: 'inventory/categories/new',
        element: <Navigate to="/inventory/categories" replace />,
      },
      {
        path: 'inventory/categories/:id/edit',
        element: (
          <RequireRouteAccess route="/inventory/categories">
            <InventoryCategoryEdit />
          </RequireRouteAccess>
        ),
      },
      {
        path: 'inventory/categories',
        element: (
          <RequireRouteAccess route="/inventory/categories">
            <InventoryCategoryEdit />
          </RequireRouteAccess>
        ),
      },

      // Inventory — warehouses (list + form; API pending)
      {
        path: 'inventory/warehouses/new',
        element: (
          <RequireRouteAccess route="/inventory/warehouses">
            <InventoryWarehouseEdit />
          </RequireRouteAccess>
        ),
      },
      {
        path: 'inventory/warehouses/:id/edit',
        element: (
          <RequireRouteAccess route="/inventory/warehouses">
            <InventoryWarehouseEdit />
          </RequireRouteAccess>
        ),
      },
      {
        path: 'inventory/warehouses',
        element: (
          <RequireRouteAccess route="/inventory/warehouses">
            <InventoryWarehouseList />
          </RequireRouteAccess>
        ),
      },

      // Inventory — adjustments (list + form; API pending)
      {
        path: 'inventory/adjustments/new',
        element: (
          <RequireRouteAccess route="/inventory/adjustments">
            <InventoryAdjustmentEdit />
          </RequireRouteAccess>
        ),
      },
      {
        path: 'inventory/adjustments/:id/edit',
        element: (
          <RequireRouteAccess route="/inventory/adjustments">
            <InventoryAdjustmentEdit />
          </RequireRouteAccess>
        ),
      },
      {
        path: 'inventory/adjustments/:id/view',
        element: (
          <RequireRouteAccess route="/inventory/adjustments">
            <InventoryAdjustmentEdit />
          </RequireRouteAccess>
        ),
      },
      {
        path: 'inventory/adjustments',
        element: (
          <RequireRouteAccess route="/inventory/adjustments">
            <InventoryAdjustmentList />
          </RequireRouteAccess>
        ),
      },

      // Inventory - movements (list + form; API pending)
      {
        path: 'inventory/movements/new',
        element: (
          <RequireRouteAccess route="/inventory/movements">
            <InventoryMovementEdit />
          </RequireRouteAccess>
        ),
      },
      {
        path: 'inventory/movements/:id/edit',
        element: (
          <RequireRouteAccess route="/inventory/movements">
            <InventoryMovementEdit />
          </RequireRouteAccess>
        ),
      },
      {
        path: 'inventory/movements',
        element: (
          <RequireRouteAccess route="/inventory/movements">
            <InventoryMovementList />
          </RequireRouteAccess>
        ),
      },

      // Inventory — unsellable movement (list + form; localStorage until API exists)
      {
        path: 'inventory/unsellable-movement/new',
        element: (
          <RequireRouteAccess route="/inventory/unsellable-movement">
            <UnsellableInventoryEdit />
          </RequireRouteAccess>
        ),
      },
      {
        path: 'inventory/unsellable-movement/:id/edit',
        element: (
          <RequireRouteAccess route="/inventory/unsellable-movement">
            <UnsellableInventoryEdit />
          </RequireRouteAccess>
        ),
      },
      {
        path: 'inventory/unsellable-movement/:id/view',
        element: (
          <RequireRouteAccess route="/inventory/unsellable-movement">
            <UnsellableInventoryEdit />
          </RequireRouteAccess>
        ),
      },
      {
        path: 'inventory/unsellable-movement',
        element: (
          <RequireRouteAccess route="/inventory/unsellable-movement">
            <UnsellableInventoryList />
          </RequireRouteAccess>
        ),
      },

      // Inventory - dispatch (list + form; API pending - localStorage)
      {
        path: 'inventory/dispatch/new',
        element: (
          <RequireRouteAccess route="/inventory/dispatch">
            <InventoryDispatchEdit />
          </RequireRouteAccess>
        ),
      },
      {
        path: 'inventory/dispatch/:id/edit',
        element: (
          <RequireRouteAccess route="/inventory/dispatch">
            <InventoryDispatchEdit />
          </RequireRouteAccess>
        ),
      },
      {
        path: 'inventory/dispatch',
        element: (
          <RequireRouteAccess route="/inventory/dispatch">
            <InventoryDispatchList />
          </RequireRouteAccess>
        ),
      },

      // Inventory - received (list + form; mock data + localStorage only — no API)
      {
        path: 'inventory/received/new',
        element: (
          <RequireRouteAccess route="/inventory/received">
            <InventoryReceivedEdit />
          </RequireRouteAccess>
        ),
      },
      {
        path: 'inventory/received/:id/edit',
        element: (
          <RequireRouteAccess route="/inventory/received">
            <InventoryReceivedEdit />
          </RequireRouteAccess>
        ),
      },
      {
        path: 'inventory/received',
        element: (
          <RequireRouteAccess route="/inventory/received">
            <InventoryReceivedList />
          </RequireRouteAccess>
        ),
      },

      // Inventory - purchase orders (list + form; API pending)
      {
        path: 'inventory/purchase-orders/new',
        element: (
          <RequireRouteAccess route="/inventory/purchase-orders">
            <InventoryPurchaseOrderEdit />
          </RequireRouteAccess>
        ),
      },
      {
        path: 'inventory/purchase-orders/:id/edit',
        element: (
          <RequireRouteAccess route="/inventory/purchase-orders">
            <InventoryPurchaseOrderEdit />
          </RequireRouteAccess>
        ),
      },
      {
        path: 'inventory/purchase-orders',
        element: (
          <RequireRouteAccess route="/inventory/purchase-orders">
            <InventoryPurchaseOrdersList />
          </RequireRouteAccess>
        ),
      },

      // Inventory - sales orders (list + form; API pending)
      {
        path: 'inventory/sales-orders/new',
        element: (
          <RequireRouteAccess route="/inventory/sales-orders">
            <InventorySalesOrderEdit />
          </RequireRouteAccess>
        ),
      },
      {
        path: 'inventory/sales-orders/:id/edit',
        element: (
          <RequireRouteAccess route="/inventory/sales-orders">
            <InventorySalesOrderEdit />
          </RequireRouteAccess>
        ),
      },
      {
        path: 'inventory/sales-orders',
        element: (
          <RequireRouteAccess route="/inventory/sales-orders">
            <InventorySalesOrdersList />
          </RequireRouteAccess>
        ),
      },

      // Inventory - delivery challan (list + form; mock/localStorage until API exists)
      {
        path: 'inventory/delivery-challan/new',
        element: (
          <RequireRouteAccess route="/inventory/delivery-challan">
            <InventoryDeliveryChallanEdit />
          </RequireRouteAccess>
        ),
      },
      {
        path: 'inventory/delivery-challan/:id/edit',
        element: (
          <RequireRouteAccess route="/inventory/delivery-challan">
            <InventoryDeliveryChallanEdit />
          </RequireRouteAccess>
        ),
      },
      {
        path: 'inventory/delivery-challan',
        element: (
          <RequireRouteAccess route="/inventory/delivery-challan">
            <InventoryDeliveryChallanList />
          </RequireRouteAccess>
        ),
      },

      // Inventory - sales quotations (SalesQuotation API list + edit by numeric id)
      {
        path: 'inventory/quotations/new',
        element: (
          <RequireRouteAccess route="/inventory/quotations">
            <InventoryQuotationEdit />
          </RequireRouteAccess>
        ),
      },
      {
        path: 'inventory/quotations/:id/edit',
        element: (
          <RequireRouteAccess route="/inventory/quotations">
            <InventoryQuotationEdit />
          </RequireRouteAccess>
        ),
      },
      {
        path: 'inventory/quotations',
        element: (
          <RequireRouteAccess route="/inventory/quotations">
            <InventoryQuotationsList />
          </RequireRouteAccess>
        ),
      },

      // Inventory - goods receipt notes (list + form; API pending)
      {
        path: 'inventory/goods-receipt-notes/new',
        element: (
          <RequireRouteAccess route="/inventory/goods-receipt-notes">
            <GoodsReceiptNotesEdit />
          </RequireRouteAccess>
        ),
      },
      {
        path: 'inventory/goods-receipt-notes/:id/edit',
        element: (
          <RequireRouteAccess route="/inventory/goods-receipt-notes">
            <GoodsReceiptNotesEdit />
          </RequireRouteAccess>
        ),
      },
      {
        path: 'inventory/goods-receipt-notes',
        element: (
          <RequireRouteAccess route="/inventory/goods-receipt-notes">
            <GoodsReceiptNotesList />
          </RequireRouteAccess>
        ),
      },

      // Inventory - goods return notes (list + form; UI only — no API)
      {
        path: 'inventory/goods-return-notes/new',
        element: (
          <RequireRouteAccess route="/inventory/goods-return-notes">
            <GoodsReturnNotesEdit />
          </RequireRouteAccess>
        ),
      },
      {
        path: 'inventory/goods-return-notes/:id/edit',
        element: (
          <RequireRouteAccess route="/inventory/goods-return-notes">
            <GoodsReturnNotesEdit />
          </RequireRouteAccess>
        ),
      },
      {
        path: 'inventory/goods-return-notes',
        element: (
          <RequireRouteAccess route="/inventory/goods-return-notes">
            <GoodsReturnNotesList />
          </RequireRouteAccess>
        ),
      },

      // Inventory — schemes & discounts (bonus schemes, discount schemes)
      {
        path: 'inventory/schemes-discounts/bonus-items/new',
        element: <BonusItemsEdit />,
      },
      {
        path: 'inventory/schemes-discounts/bonus-items/:id/edit',
        element: <BonusItemsEdit />,
      },
      {
        path: 'inventory/schemes-discounts/bonus-items',
        element: <BonusItemsList />,
      },
      {
        path: 'inventory/schemes-discounts/discounts/new',
        element: <InventoryDiscountsEdit />,
      },
      {
        path: 'inventory/schemes-discounts/discounts/:id/edit',
        element: <InventoryDiscountsEdit />,
      },
      {
        path: 'inventory/schemes-discounts/discounts',
        element: <InventoryDiscountsList />,
      },

      {
        path: 'inventory/reports/cogs',
        element: (
          <RequireRouteAccess route="/inventory/reports/cogs">
            <CogsReport />
          </RequireRouteAccess>
        ),
      },
      {
        path: 'inventory/reports/cogs/print',
        element: <CogsReportPrint />,
      },
      {
        path: 'inventory/reports/stock',
        element: (
          <RequireRouteAccess route="/inventory/reports/stock">
            <StockReport />
          </RequireRouteAccess>
        ),
      },
      {
        path: 'inventory/reports/stock/print',
        element: <StockReportPrint />,
      },
      {
        path: 'inventory/reports/sales',
        element: (
          <RequireRouteAccess route="/inventory/reports/sales">
            <SalesReport />
          </RequireRouteAccess>
        ),
      },
      {
        path: 'inventory/reports/sales/print',
        element: <SalesReportPrint />,
      },

      // Supply Chain - purchase requisitions (list + form; localStorage until API exists)
      {
        path: 'supply-chain/purchase-requisitions/new',
        element: <PurchaseRequisitionEdit />,
      },
      {
        path: 'supply-chain/purchase-requisitions/:id/edit',
        element: <PurchaseRequisitionEdit />,
      },
      {
        path: 'supply-chain/purchase-requisitions',
        element: <PurchaseRequisitionsList />,
      },

      // Supply Chain - purchase orders (list + form; localStorage until API exists)
      {
        path: 'supply-chain/purchase-orders/new',
        element: <SupplyChainPurchaseOrderEdit />,
      },
      {
        path: 'supply-chain/purchase-orders/:id/edit',
        element: <SupplyChainPurchaseOrderEdit />,
      },
      {
        path: 'supply-chain/purchase-orders',
        element: <SupplyChainPurchaseOrdersList />,
      },

      // Supply Chain - goods receipt notes (list + form; localStorage until API exists)
      {
        path: 'supply-chain/goods-receipt-notes/new',
        element: <SupplyChainGoodsReceiptNotesEdit />,
      },
      {
        path: 'supply-chain/goods-receipt-notes/:id/edit',
        element: <SupplyChainGoodsReceiptNotesEdit />,
      },
      {
        path: 'supply-chain/goods-receipt-notes',
        element: <SupplyChainGoodsReceiptNotesList />,
      },

      // Legacy /items → inventory items list
      {
        path: 'items',
        element: <Navigate to="/inventory/items" replace />,
      },

      // Sales Inventory Module Routes (stocks, etc.)
      {
        path: 'stocks',
        element: (
          <RequireRouteAccess route="/stocks">
            <Stocks />
          </RequireRouteAccess>
        ),
      },
      {
        path: 'vendors',
        element: (
          <RequireRouteAccess route="/vendors">
            <Vendors />
          </RequireRouteAccess>
        ),
      },
      {
        path: 'warehouses',
        element: <Navigate to="/inventory/warehouses" replace />,
      },
      {
        path: 'point-of-sales',
        element: (
          <RequireRouteAccess route="/point-of-sales">
            <PointOfSales />
          </RequireRouteAccess>
        ),
      },

      // Workflow / Approval Master
      {
        path: 'workflow',
        children: [
          {
            path: 'approval-master',
            element: (
              <RequireRouteAccess route="/workflow/approval-master">
                <ApprovalMasterDetail />
              </RequireRouteAccess>
            ),
          },
        ],
      },
      {
        path: 'approval-templates',
        element: (
          <RequireRouteAccess route="/approval-templates">
            <ApprovalTemplates />
          </RequireRouteAccess>
        ),
      },
      {
        path: 'approval-jobs',
        element: (
          <RequireRouteAccess route="/approval-jobs">
            <Outlet />
          </RequireRouteAccess>
        ),
        children: [
          { index: true, element: <ApprovalJobs /> },
          {
            path: ':id',
            element: <ApprovalDetail />,
          },
        ],
      },

      // Settings Module Routes
      {
        path: 'security',
        children: [
          {
            path: 'roles',
            children: [
              {
                index: true,
                element: (
                  <RequireRouteAccess route="/security/users">
                    <RolesList />
                  </RequireRouteAccess>
                ),
              },
              {
                path: 'new',
                element: (
                  <RequireRouteAccess route="/security/users">
                    <RoleDetail />
                  </RequireRouteAccess>
                ),
              },
              {
                path: ':id',
                element: (
                  <RequireRouteAccess route="/security/users">
                    <RoleDetail />
                  </RequireRouteAccess>
                ),
              },
            ],
          },
          {
            path: 'users',
            children: [
              {
                index: true,
                element: (
                  <RequireRouteAccess route="/security/users">
                    <UsersList />
                  </RequireRouteAccess>
                ),
              },
              {
                path: 'new',
                element: (
                  <RequireRouteAccess route="/security/users">
                    <UserDetail />
                  </RequireRouteAccess>
                ),
              },
              {
                path: ':id',
                element: (
                  <RequireRouteAccess route="/security/users">
                    <UserDetail />
                  </RequireRouteAccess>
                ),
              },
            ],
          },
        ],
      },

      {
        path: 'lookups',
        element: (
          <RequireRouteAccess route="/lookups">
            <Lookups />
          </RequireRouteAccess>
        ),
      },
      {
        path: 'taxes',
        element: (
          <RequireRouteAccess route="/taxes">
            <Taxes />
          </RequireRouteAccess>
        ),
      },
      {
        path: 'banks',
        element: (
          <RequireRouteAccess route="/banks">
            <Banks />
          </RequireRouteAccess>
        ),
      },
      {
        path: 'accounts',
        element: (
          <RequireRouteAccess route="/accounts">
            <Accounts />
          </RequireRouteAccess>
        ),
      },
      {
        path: 'branches',
        children: [
          {
            index: true,
            element: (
              <RequireRouteAccess route="/branches">
                <BranchesList />
              </RequireRouteAccess>
            ),
          },
          {
            path: 'new',
            element: (
              <RequireRouteAccess route="/branches">
                <BranchesEdit />
              </RequireRouteAccess>
            ),
          },
          {
            path: ':id/view',
            element: (
              <RequireRouteAccess route="/branches">
                <BranchesView />
              </RequireRouteAccess>
            ),
          },
          {
            path: ':id/edit',
            element: (
              <RequireRouteAccess route="/branches">
                <BranchesEdit />
              </RequireRouteAccess>
            ),
          },
        ],
      },
      {
        path: 'company-banks',
        children: [
          {
            index: true,
            element: (
              <RequireRouteAccess route="/company-banks">
                <CompanyBanksList />
              </RequireRouteAccess>
            ),
          },
          {
            path: 'new',
            element: (
              <RequireRouteAccess route="/company-banks">
                <CompanyBanksDetail />
              </RequireRouteAccess>
            ),
          },
          {
            path: ':id/edit',
            element: (
              <RequireRouteAccess route="/company-banks">
                <CompanyBanksDetail />
              </RequireRouteAccess>
            ),
          },
        ],
      },
      {
        path: 'notifications',
        element: (
          <RequireRouteAccess route="/notifications">
            <Notifications />
          </RequireRouteAccess>
        ),
      },
      {
        path: 'late-policy',
        element: <LatePolicy />,
      },
      {
        path: 'leave-policy',
        element: <LeavePolicy />,
      },
    ],
  },

  // 404 fallback
  {
    path: '*',
    element: <NotFoundPage />,
  },
]) 